<template>
  <div class="puck-shell" :style="shellStyle">
    <span v-if="puckArtwork" class="puck-artwork" aria-hidden="true" v-html="puckArtwork" />

    <template v-if="interactive">
      <button
        v-for="well in wells"
        :key="well.no"
        type="button"
        class="well-target"
        :class="{
          occupied: well.occupied,
          selected: well.no === selectedWell,
          'drag-source': dragSourceWellNo === well.no,
          'drag-over': dragOverWellNo === well.no && canDropOnWell(well),
          'drop-blocked': dragSourceWellNo !== null && dragSourceWellNo !== well.no && well.occupied,
        }"
        :style="wellStyle(well)"
        :draggable="canDragWell(well)"
        :aria-grabbed="dragSourceWellNo === well.no ? 'true' : undefined"
        :aria-label="wellAriaLabel(well)"
        @click="$emit('select-well', well.no)"
        @dragstart="onDragStart($event, well)"
        @dragend="onDragEnd"
        @dragenter.prevent="onDragEnter($event, well)"
        @dragover.prevent="onDragOver($event, well)"
        @dragleave="onDragLeave($event, well)"
        @drop.prevent="onDrop($event, well)"
      >
        <WellBox :well="well" />
      </button>
    </template>

    <template v-else>
      <span
        v-for="well in wells"
        :key="well.no"
        class="well-target"
        :class="{
          occupied: well.occupied,
          selected: well.no === selectedWell,
        }"
        :style="wellStyle(well)"
        :aria-label="wellAriaLabel(well)"
      >
        <WellBox :well="well" />
      </span>
    </template>
  </div>
</template>

<script>
let cachedPuckArtwork = "";
let pendingPuckArtwork = null;

function loadPuckArtwork() {
  if (cachedPuckArtwork) return Promise.resolve(cachedPuckArtwork);
  if (!pendingPuckArtwork) {
    pendingPuckArtwork = fetch("/assets/images/puck-real-preview.svg")
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load puck artwork: ${response.status}`);
        return response.text();
      })
      .then((svg) => {
        cachedPuckArtwork = svg;
        return svg;
      })
      .catch(() => "");
  }
  return pendingPuckArtwork;
}
</script>

<script setup>
import { computed, defineComponent, h, onMounted, ref, useId } from "vue";
import ClippedBox from "../manage-products-sections/clipped.vue";
import UnclippedBox from "../manage-products-sections/unclipped.vue";
import OpenCupBox from "../manage-products-sections/opencup.vue";
import Radial12Box from "../manage-products-sections/radial12.vue";
import appStore from "../../../../store/index.js";

const props = defineProps({
  color: {
    type: String,
    default: "#ef8a54",
  },
  selectedWell: {
    type: Number,
    default: 1,
  },
  occupiedWells: {
    type: Array,
    default: () => [],
  },
  wellColors: {
    type: Object,
    default: () => ({}),
  },
  wellRows: {
    type: Array,
    default: () => [],
  },
  interactive: {
    type: Boolean,
    default: true,
  },
  puckId: {
    type: String,
    default: "",
  },
  canMoveBoxes: {
    type: Boolean,
    default: false,
  },
  showBoxTitlePreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select-well", "move-box"]);

const wellCoordinates = [
  [1, 37.4, 20.5],
  [2, 62.4, 20.5],
  [3, 25.7, 40.0],
  [4, 49.9, 40.0],
  [5, 74.2, 40.0],
  [6, 14.6, 59.8],
  [7, 38.2, 59.8],
  [8, 61.7, 59.8],
  [9, 85.3, 59.8],
  [10, 25.7, 79.3],
  [11, 49.9, 79.3],
  [12, 74.2, 79.3],
];

const occupiedSet = computed(() => new Set(props.occupiedWells.map((well) => Number(well))));
const rowByWell = computed(() => {
  const rows = new Map();
  props.wellRows.forEach((row) => rows.set(Number(row.no), row));
  return rows;
});
const dragSourceWellNo = ref(null);
const dragOverWellNo = ref(null);
const dragEnterCounters = ref(new Map());
const rawPuckArtwork = ref("");
const svgId = `puck-redesign-${useId().replace(/[^a-zA-Z0-9_-]/g, "-")}`;

onMounted(async () => {
  rawPuckArtwork.value = await loadPuckArtwork();
});

const wells = computed(() =>
  wellCoordinates.map(([no, x, y]) => {
    const row = rowByWell.value.get(no);
    const box = row?.box || null;
    return {
      no,
      x,
      y,
      box,
      gridCount: Number(row?.gridCount || 0),
      gridStates: Array.isArray(row?.gridStates) ? row.gridStates : null,
      occupied: Boolean(box) || occupiedSet.value.has(no),
      color: box ? boxFillColor(box) : props.wellColors?.[no] || "#22c55e",
    };
  })
);

function normalizeColor(value) {
  if (!value) return appStore.transformPuckColor("orange");
  const trimmed = String(value).trim();
  return appStore.transformPuckColor(trimmed);
}

function darkerPuckColor(value) {
  if (!value) return appStore.transformPuckColor("orange", true);
  const trimmed = String(value).trim();
  return appStore.transformPuckColor(trimmed, true);
}

function wellAriaLabel(well) {
  const state = well.no === props.selectedWell ? "selected" : well.occupied ? "occupied" : "empty";
  const title = props.showBoxTitlePreview ? boxTitleLabel(well.box) : "";
  return title ? `Well ${well.no} - ${state} - ${title}` : `Well ${well.no} - ${state}`;
}

function boxType(box) {
  return String(box?.boxType || box?.format || "").toLowerCase();
}

function boxComponent(box) {
  const type = boxType(box);
  if (type.includes("12-grid") || type.includes("12x12") || String(box?.format || "").toLowerCase() === "box_12" || Number(box?.slotCount) === 12) return Radial12Box;
  if (type.includes("clip") && !type.includes("unclip")) return ClippedBox;
  if (type.includes("open") || type.includes("hpf")) return OpenCupBox;
  return UnclippedBox;
}

function boxVisualClass(box) {
  const type = boxType(box);
  if (type.includes("12-grid") || type.includes("12x12") || String(box?.format || "").toLowerCase() === "box_12" || Number(box?.slotCount) === 12) return "well-radial12";
  return type.includes("clip") && !type.includes("unclip") ? "well-clipped" : "well-unclipped";
}

function boxColorClass(box) {
  const color = String(box?.color || "gold").trim().replace(/\s+/g, "-");
  return `well-${color}-${boxVisualClass(box).replace("well-", "")}`;
}

function boxFillColor(box) {
  return appStore.transformBoxColor(String(box?.color || "gold"));
}

function boxTitleLabel(box) {
  return String(box?.title || "").trim();
}

function boxDarkColor(box) {
  return appStore.transformBoxColor(String(box?.color || "gold"), true);
}

function gridClipState(well) {
  if (Array.isArray(well.gridStates)) {
    return Array.from({ length: Math.max(1, Number(well.box?.slotCount || well.gridStates.length || 4)) }, (_, index) => {
      const grid = well.gridStates[index];
      if (!grid) return false;
      return {
        present: true,
        tossed: isTossedGrid(grid),
      };
    });
  }
  const slotCount = Math.max(1, Number(well.box?.slotCount || 4));
  const count = Math.max(0, Math.min(slotCount, Number(well.gridCount || 0)));
  return Array.from({ length: slotCount }, (_, index) => (index < count ? { present: true, tossed: false } : false));
}

function isTossedGrid(grid) {
  const type = grid?.gridType;
  if (Array.isArray(type) && Boolean(type[2])) return true;
  return String(grid?.status || "").toLowerCase() === "tossed";
}

function wellStyle(well) {
  return {
    left: `${well.x}%`,
    top: `${well.y}%`,
    "--well-color": well.color,
    "--box-fill": well.box ? boxFillColor(well.box) : "#e4edf7",
    "--box-dark": well.box ? boxDarkColor(well.box) : "#64748b",
  };
}

function canDragWell(well) {
  return Boolean(props.canMoveBoxes && props.puckId && well.box?.id);
}

function canDropOnWell(well) {
  return Boolean(
    props.canMoveBoxes &&
    props.puckId &&
    dragSourceWellNo.value !== null &&
    dragSourceWellNo.value !== well.no &&
    !well.occupied,
  );
}

function setDropEffect(event, effect) {
  if (event.dataTransfer) event.dataTransfer.dropEffect = effect;
}

function onDragStart(event, well) {
  if (!canDragWell(well)) {
    event.preventDefault();
    return;
  }
  dragSourceWellNo.value = well.no;
  dragOverWellNo.value = null;
  dragEnterCounters.value = new Map();
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/x-inventory-box-id", String(well.box.id));
    event.dataTransfer.setData("text/plain", String(well.box.id));
  }
}

function onDragEnd() {
  dragSourceWellNo.value = null;
  dragOverWellNo.value = null;
  dragEnterCounters.value = new Map();
}

function onDragEnter(event, well) {
  if (!canDropOnWell(well)) {
    setDropEffect(event, "none");
    return;
  }
  setDropEffect(event, "move");
  const next = new Map(dragEnterCounters.value);
  const count = Number(next.get(well.no) || 0) + 1;
  next.set(well.no, count);
  dragEnterCounters.value = next;
  if (count === 1) dragOverWellNo.value = well.no;
}

function onDragOver(event, well) {
  setDropEffect(event, canDropOnWell(well) ? "move" : "none");
}

function onDragLeave(event, well) {
  const next = new Map(dragEnterCounters.value);
  const count = Math.max(0, Number(next.get(well.no) || 0) - 1);
  if (count === 0) {
    next.delete(well.no);
    if (dragOverWellNo.value === well.no) dragOverWellNo.value = null;
  } else {
    next.set(well.no, count);
  }
  dragEnterCounters.value = next;
}

function onDrop(event, well) {
  if (!canDropOnWell(well)) {
    onDragEnd();
    return;
  }
  const boxId = event.dataTransfer?.getData("text/x-inventory-box-id") || event.dataTransfer?.getData("text/plain") || "";
  const sourceWellNo = dragSourceWellNo.value;
  if (!boxId || sourceWellNo === null || sourceWellNo === well.no) {
    onDragEnd();
    return;
  }
  emit("move-box", {
    boxId,
    sourcePuckId: props.puckId,
    targetPuckId: props.puckId,
    sourceWellNo,
    targetWellNo: well.no,
  });
  onDragEnd();
}

const WellBox = defineComponent({
  name: "WellBox",
  props: {
    well: {
      type: Object,
      required: true,
    },
  },
  setup(componentProps) {
    return () => {
      const well = componentProps.well;
      if (well.box) {
        const label = props.showBoxTitlePreview ? boxTitleLabel(well.box) : "";
        return h(
          "span",
          { class: "well-box occupied", "aria-hidden": "true" },
          [
            h(boxComponent(well.box), {
              class: ["box-asset", boxVisualClass(well.box), boxColorClass(well.box)],
              color: boxFillColor(well.box),
              darkColor: boxDarkColor(well.box),
              wellNumber: well.no,
              showGridClips: gridClipState(well),
            }),
            label
              ? h("span", { class: "well-box-title", title: label }, label)
              : null,
          ],
        );
      }
      return h(
        "span",
        { class: "well-box empty", "aria-hidden": "true" },
        [h("img", { src: "/assets/images/emptybox.svg", alt: "", class: "empty-box-image", draggable: "false" })],
      );
    };
  },
});

const shellStyle = computed(() => ({
  "--puck-accent": normalizeColor(props.color),
  "--puck-accent-dark": darkerPuckColor(props.color),
}));

const puckArtwork = computed(() => {
  if (!rawPuckArtwork.value) return "";
  return rawPuckArtwork.value
    .replace(/id="pucksvg-3"/, `id="${svgId}"`)
    .replace(/#pucksvg-3/g, `#${svgId}`)
    .replace(/#FFC0CB/g, normalizeColor(props.color))
    .replace(/rgb\(\s*175,\s*112,\s*123\s*\)/g, darkerPuckColor(props.color))
    .replace(/href="assets\/images\/emptybox.svg"/g, 'href="/assets/images/emptybox.svg"')
    .replace(/\sdata-v-33ac49d7=""/g, "");
});
</script>

<style scoped>
.puck-shell {
  position: relative;
  width: min(100%, 430px);
  aspect-ratio: 407.54 / 435.08;
  isolation: isolate;
}

.puck-shell::before {
  position: absolute;
  content: "";
  border-radius: 999px;
  inset: 9% 8% 6%;
  pointer-events: none;
}

.puck-shell::before {
  background: transparent;
  filter: none;
  opacity: 0;
  z-index: -1;
}

.puck-artwork {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 24px 30px rgb(15 23 42 / 0.24));
  pointer-events: none;
  user-select: none;
  z-index: 1;
}

.puck-artwork :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.puck-artwork :deep(.st0) {
  fill: var(--puck-accent) !important;
}

.puck-artwork :deep(.st1) {
  fill: var(--puck-accent-dark) !important;
}

.puck-artwork :deep(.st2) {
  fill: #ffffff !important;
}

.puck-artwork :deep(.st3) {
  fill: none !important;
}

.well-target {
  position: absolute;
  width: 20.85%;
  aspect-ratio: 1;
  border: 0;
  border-radius: 999px;
  background: transparent;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 5;
}

.well-target:hover,
.well-target.selected {
  z-index: 18;
}

.well-target::before {
  position: absolute;
  content: "";
  inset: 1%;
  border: 2px solid transparent;
  border-radius: inherit;
  z-index: 3;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease,
    background 120ms ease;
}

.well-target:hover::before {
  border-color: rgb(14 165 233 / 0.62);
  background: rgb(14 165 233 / 0.08);
}

.well-target.selected::before {
  border-color: #0ea5e9;
  background: rgb(14 165 233 / 0.14);
  box-shadow:
    0 0 0 5px rgb(14 165 233 / 0.14),
    inset 0 0 0 1px rgb(255 255 255 / 0.72);
}

.well-target[draggable="true"]:not(.drag-source) {
  cursor: grab;
}

.well-target.drag-source {
  cursor: grabbing;
  opacity: 0.42;
}

.well-target.drag-source::before {
  border-color: #0ea5e9;
  border-style: dashed;
}

.well-target.drag-over::before {
  border-color: #16a34a;
  background: rgb(22 163 74 / 0.18);
  box-shadow: 0 0 0 4px rgb(22 163 74 / 0.2);
}

.well-target.drop-blocked {
  cursor: not-allowed;
}

.well-target.drop-blocked::before {
  border-color: #ef4444;
  background: rgb(239 68 68 / 0.1);
}

.well-box {
  position: absolute;
  inset: 1.1%;
  display: block;
  pointer-events: none;
  z-index: 20;
}

.well-box.occupied {
  filter: drop-shadow(0 4px 8px rgb(15 23 42 / 0.25));
}

.well-target :deep(.well-box-title) {
  position: absolute;
  top: -4%;
  left: 50%;
  z-index: 60;
  width: 84px;
  max-width: 84px;
  transform: translate(-50%, -70%);
  border: 1px solid rgb(30 41 59 / 0.82);
  border-radius: 2px;
  background: rgb(255 255 255 / 0.88);
  color: #1e293b;
  font-size: 0.58rem;
  font-weight: 600;
  line-height: 1.1;
  padding: 2px 4px;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-shadow:
    0 1px 2px rgb(15 23 42 / 0.2),
    0 0 0 1px rgb(255 255 255 / 0.7);
  transition:
    max-width 140ms ease,
    width 140ms ease,
    min-height 140ms ease,
    background 140ms ease,
    transform 140ms ease;
}

.well-target:hover :deep(.well-box-title) {
  width: max-content;
  min-width: 84px;
  max-width: 168%;
  min-height: 0;
  background: rgb(255 255 255 / 0.98);
  line-height: 1.15;
  overflow: hidden;
  text-overflow: clip;
  white-space: normal;
  transform: translate(-50%, -86%);
  box-shadow:
    0 8px 18px rgb(15 23 42 / 0.24),
    0 0 0 1px rgb(15 23 42 / 0.16);
}

.well-box.empty {
  opacity: 0.2;
}

.well-target :deep(.box-asset),
.well-target :deep(.empty-box-image) {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.well-box :deep(.box-asset.well-clipped .st0) {
  fill: var(--box-fill, #e4edf7) !important;
}

.well-box :deep(.box-asset.well-clipped .st1) {
  fill: var(--box-dark, #64748b) !important;
}

.well-box :deep(.box-asset.well-unclipped .st0) {
  fill: var(--box-dark, #64748b) !important;
}

.well-box :deep(.box-asset.well-unclipped .st1) {
  fill: var(--box-fill, #e4edf7) !important;
}

.well-box :deep(.box-asset.well-radial12 .st0) {
  fill: var(--box-fill, #e4edf7) !important;
}

.well-box :deep(.box-asset.well-radial12 .st1) {
  fill: var(--box-dark, #64748b) !important;
}

.well-box :deep(.box-asset.well-white-clipped .st2),
.well-box :deep(.box-asset.well-white-unclipped .st2) {
  stroke: var(--box-dark, #a8a8a8) !important;
  stroke-width: 0.8px !important;
  stroke-linejoin: round;
}

.well-box :deep(.box-asset.well-white-radial12 .radial12-body) {
  stroke: var(--box-dark, #a8a8a8) !important;
  stroke-width: 1.6px !important;
  stroke-linejoin: round;
}

.well-box :deep(.box-asset .stblack) {
  fill: #0f172a !important;
}

.well-box :deep(.box-asset .tossed-grid) {
  fill: #ff0000 !important;
  stroke: #fff3f3 !important;
  stroke-width: 0.35px !important;
}

</style>
